import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { useSnackbar } from '@/modules/core/hooks';

import { QUERY_KEY as CATEGORIES_QUERY_KEY } from '../../../queries/useGetCategories.query';
import { ServiceTypeValues } from '../../../Services.types';

import { QUERY_KEY as SERVICES_QUERY_KEY } from './useGetServicesByCategory.query';

interface HideServiceParams {
  id: string;
  serviceType: typeof ServiceTypeValues.HIDDEN | typeof ServiceTypeValues.MANUAL_CLAIM;
}

const useHideServiceQuery = () => {
  const queryClient = useQueryClient();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const { mutateAsync, isLoading } = useMutation(
    ({ id, serviceType }: HideServiceParams) =>
      api.patch(API_URL.SERVICES_BY_ID(id), {
        service_type: serviceType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [SERVICES_QUERY_KEY] });
      },
    }
  );

  const handleHideService = useCallback(
    async (id: string, serviceName: string) => {
      await mutateAsync({ id, serviceType: ServiceTypeValues.HIDDEN });
      handleAddPermanentSnackbar({
        text: `${serviceName} service was hidden`,
        closeIcon: true,
      });
    },
    [mutateAsync, handleAddPermanentSnackbar]
  );

  const handleUnhideService = useCallback(
    async (id: string, serviceName: string) => {
      await mutateAsync({ id, serviceType: ServiceTypeValues.MANUAL_CLAIM });
      handleAddPermanentSnackbar({
        text: `${serviceName} service was unhidden`,
        closeIcon: true,
      });
    },
    [mutateAsync, handleAddPermanentSnackbar]
  );

  return { handleHideService, handleUnhideService, isLoading };
};

export default useHideServiceQuery;
